const _ = require("lodash");
const setIn = require("./ImmHelpers").setIn;
const TrainerCore = require("./TrainerCore");
const SharedErrorLog = require("./SharedErrorLog");

// ResultManagers provide the implementation for the special behavior that
// results have based on their resultType. Each resultType has a file in the
// results folder which overrides some of the settings and methods below.
// Please see the google doc for more details on what each setting and method does:
// https://docs.google.com/document/d/1Y-c5GX8pirQdR9sGNAjeRVAoRAXOJFL7CbjKfRl0lWk/edit

// To define a new result type, create a file in the results folder and add it
// to the specificManagerData object below. In this file, export a definition for
// each method that result type should override; only names appearing in
// defaultMethods below will be used. You also may export an object called
// settings which will override any of the options in defaultSettings below.

//
// Result Manager settings
//

const defaultSettings = {
	//NAVIGATING BETWEEN PROBLEMS:
	gotoSummaryWhenFinished: false, //If true, finishing the block will immediately bring you to the summary page
	hasNoSummary: false, //If true, make sure the user is never taken to a summary page
	allowBrowseOnSummary: false, //Instead of showing Result button on each problem, Next is shown to allow browsing through solutions.

	//STAR SETTINGS/CALCULATIONS
	staticMaxStars: false, //If true, the max stars for this block will always be 3 no matter how many times it is played.
	canEarnStarsOnStateChange: false, //If true, every time the state changes, an action will occur to check if a new star has been earned.
	canEarnStarsOnTrialSubmission: false, //If true, every time a trial is submitted, an action will occur to check if a new star has been earned
	showMessageOnStarEarned: false, //If the above is true, set to true if a slider message should appear notifying the student a star has been earned

	//TRIAL/ATTEMPT SETTINGS:
	infiniteAttempts: false, //If true, all problems with this result will have infinite attempts
	limitAttemptSpeed: false, //If true, attempts less than a very short period (like 5 seconds apart) will be rejected with a message that they should wait before submitting
	allowRepeatTrials: false, //If true, problem submission won't check for repeat trials and will allow previous trials to be submitted again.
	neverShowSliderMessages: false, //If true, the BA character slider messages won't pop out after problem submission. Note that this can still be overridden by setting force=true in the triggerMessage function.
	canReplayProblem: false, //If true, teachers/admins can replay problems in this result which resets their customState to the last trial. Should only be true for simple result types.

	//SAVING/PROBLEM STATE SETTINGS:
	neverResumeResult: false, //If true, a new result will be created every time you go to this block (so you can never resume a previous result)
	updatePercentCompleteOnStateChange: false, //If true, every time a problem's state changes, the percent complete of the block will be recalculated
	canFinishOnStateChange: false, //If true, evey time a problem's state changes, a check will be made to see if the block has been finished.
	noUndoRedoClear: false, //If true, the state of problems in this result cannot be cleared, undone, or redone. (Applies to most game results.)
	neverSetOutcome: false, //Should apply to problems that generate many subproblems. Makes sure the outcome used to determine whether the problem is complete is never set.
	neverGiveUp: false, // Don't allow giving up on problems in this result.
	saveAllProblems: false, // Save data for all problems, even if not started\
	callPostSubmissionProcessingOnStateChange: false, // Call ResultManager.processPostSubmission on state change

	//COMPLETION SETTINGS
	markCompletedAfterFirstTrial: false, //If true, mark the result completed after the first trial and ignore completed when updating

	//VIEW SETTINGS:
	starProgressType: "standard", //If ["standard", "test", "trophy", "lab"], the type of star progress view.

	pauseWhenFailing: false, // If true, the Banvas pauses immediately when there are connection or server problems.
	canSwitchSet: false, //If true, this result type has multiple sets and teachers can switch between them
	canRestartSet: true, //If true, teachers can restart results for this type
	noProblemHistory: false, //If true, don't show set progress/problem history for last completed set
	skipProblemCount: false, //If true, skip counting problems in this result type as part of the total problem and problems correct count on student stats.
};

//
// Result Manager methods
//

// Helpers for default method implementations

//Getting all problems that have at least one submitted trial
const getCompletedProblems = function(result) {
	return result.problems.filter(p => p.outcome);
};

const getFirstWithoutOutcome = function(result) {
	if (!result.problems || !result.problems.length) {
		return 0;
	}
	let index = _.findIndex(result.problems, p => !p.outcome);
	if (index < 0) {
		index = result.problems.length - 1;
	}
	return Math.min(Math.max(index, 0), result.problems.length - 1);
};

const markCurrentAsStarted = function(result, now) {
	if (
		result.problems[result.currentProblemIndex] &&
		!result.problems[result.currentProblemIndex].started
	) {
		return setIn(result, ["problems", result.currentProblemIndex], problem => {
			problem = _.clone(problem);
			problem.started = now;
			problem.startedThisSession = true;
			problem.hasMarkedStarted = true;
			return problem;
		});
	} else {
		return result;
	}
};

const setCurrentProblem = function(result, newIndex, now) {
	newIndex = Math.min(Math.max(newIndex, 0), result.problems.length - 1);
	return markCurrentAsStarted(
		setIn(result, ["currentProblemIndex"], newIndex),
		now
	);
};

const getLastPossibleIndex = function(result) {
	return result.problems.length - 1;
};

const getNumLeft = function(result) {
	return getNumProblems(result) - getCompletedProblems(result).length;
};

const getNumCorrect = function(result) {
	return result.problems.filter(p => p.outcome === "correct").length;
};

const getNumProblems = function(result) {
	return result.problems.length;
};

const canGoToNextProblem = function(result) {
	return result.currentProblemIndex < result.problems.length - 1;
};

// This function is currently only used for stat resyncing.
// It returns XP earned for the block from the backend progress.
// The XP does not include XP earned for completing the block.
const getResultProgressXP = function(backendResult) {
	const {progress} = backendResult;
	const numCorrect = getNumCorrect(progress);
	return numCorrect * getTrialXP();
};

const getTrialXP = function(result) {
	return TrainerCore.DEFAULT_TRIAL_XP;
};

const defaultMethods = {
	//
	// Methods that do nothing by default.
	//

	// Modifies the result on construction.
	initialize: (result, processor) => result,

	// Modifies the result after a trial is submitted and processed.
	processPostSubmission: (result, processor, fromStateChange) => result,

	//
	// Methods with all default behavior contained here.
	//

	// Return the percent of the result completed, used for showing partially
	// finished blocks in the chapter pages.
	getPercentComplete: function(result) {
		return getCompletedProblems(result).length / result.problems.length;
	},

	// Return whether the result is considered finished.
	isFinished: function(result) {
		// Error logging to better figure out how
		// the bug in T52077 was triggered.
		if (!result.problems) {
			SharedErrorLog.logError(
				"E_NO_RESULT_PROBLEMS",
				"result has falsy problems property",
				{currentProblemIndex: result.currentProblemIndex}
			);
		}
		return result.problems.every(p => p.outcome);
	},

	// Get the first problem index that can be accessed. The behavior is pretty
	// trivial here except for test results.
	getFirstAvailableIndex: function(result) {
		return 0;
	},

	// Get the last problem index that can be accessed.
	getLastAvailableIndex: function(result) {
		return getFirstWithoutOutcome(result);
	},

	getLastPossibleIndex: getLastPossibleIndex,

	// Get the first problem index that is incomplete. (Review only.)
	getFirstIncompleteIndex: getFirstWithoutOutcome,

	// Get the problem the result is currently on.
	getCurrentProblem: function(result) {
		return result && result.problems[result.currentProblemIndex];
	},

	//Get the version of what is now the current problem from an oldResult
	getEquivalentCurrentProblem: function(result, oldResult) {
		return oldResult.problems[result.currentProblemIndex];
	},

	//Marks the current problem as started
	markCurrentAsStarted,

	// Called when the user goes to a specific problem in the result, triggered
	// by clicking links or the set progress bar at the top.
	// This has some default behavior in OpsResult, where the index being
	// "summary" triggers the summary page to show instead.
	setCurrentProblem,

	// Called when the user goes to the next problem in the result.
	// This has some default behavior in OpsResult, where finished results go
	// to the summary instead of calling this.
	goToNextProblem: function(result, now) {
		if (
			!canGoToNextProblem(result) &&
			(result.showSummary ||
				result.showOverview ||
				result.showTestPart1Decision)
		) {
			const newIndex = result.showSummary ? "summary" : "overview";
			const newResult = setIn(result, ["isOnFinal"], true);
			return setIn(newResult, ["currentProblemIndex"], newIndex);
		} else {
			return setCurrentProblem(result, result.currentProblemIndex + 1, now);
		}
	},

	// Called when the user goes to the previous problem in the result.
	// Also goes to summary if result is finished instead of calling this.
	goToPreviousProblem: function(result, now) {
		return setCurrentProblem(result, result.currentProblemIndex - 1, now);
	},

	// Called after a trial submission to decide if the result should be intervened
	// (marked as intervened in the database and the student not allowed to continue)
	shouldIntervene: function(result) {
		return false;
	},

	// Called when the user goes to the next problem, previous problem, or
	// navigates to any other problem to check if any changes were made that
	// should trigger the instructions to appear again. Currently, this only
	// applies when a student begins a problem past an index where middle
	// instructions appear
	showMidInstructionsOnPageChange: function(result, oldResult) {
		const midIndex = result.middleInstructionsIndex;
		//There are middle instructions
		return (
			midIndex &&
			//We haven't started any problems past the middle point before or tried to start any problems past the middle point before
			(!oldResult ||
				oldResult.problems.filter(
					(p, ind) => ind >= midIndex && (p.started || p.hasMarkedStarted)
				).length === 0) &&
			//We just started at least one problem past the middle point
			result.problems.filter(
				(p, ind) => ind >= midIndex && p.startedThisSession
			).length > 0
		);
	},

	//This decides if a result should show instructions
	showInstructionsOnNavigation: function(result) {
		return ResultManager.getPercentComplete(result) === 0;
	},

	// Returns whether to enable the next problem button.
	canGoToNextProblem,

	// Returns whether to enable the previous problem button.
	canGoToPreviousProblem: function(result) {
		return result.currentProblemIndex > 0;
	},

	// Returns whether the current problem is done and should show a solution.
	isCurrentProblemDone: function(result) {
		const currentProblem = getManager(result).getCurrentProblem(result);
		return currentProblem && !!currentProblem.outcome;
	},

	//Returns the starCutoffs only with cutoffs for available stars
	getAvailableStarCutoffs: function(result) {
		if (result.starsAvailable === 3) {
			return result.starCutoffs;
		}
		// PROJ-1758: just one star available (e.g. during cooldown), keep at first star's cutoff
		return result.starCutoffs.slice(-1);
	},

	// Returns the number of stars earned in the result so far.
	getStars: function(result) {
		const progress = result.problems.map(p => p.outcome === "correct");
		return TrainerCore.getStars(
			progress,
			getManager(result).getAvailableStarCutoffs(result)
		);
	},

	// Returns the XP to award for a trial.
	getTrialXP,

	// Returns whether the trophy problem should be unlocked given this result.
	isTrophyUnlocked: function(result) {
		const stars = getManager(result).getStars(result);
		return stars >= 1;
	},

	// Returns the text on the summary page tells the user how many problems they got right.
	getSummaryProblemsSolvedText: function(result) {
		const rm = getManager(result);
		return (
			"You answered " +
			rm.getNumCorrect(result) +
			" out of " +
			rm.getNumProblems(result) +
			" correctly."
		);
	},

	// Returns the summary view to use and any data to pass to it.
	getSummaryInfo: function(result) {
		return {
			viewType: "standard",
			data: {
				problemsSolvedText: ResultManager.getSummaryProblemsSolvedText(result),
			},
		};
	},

	getNumLeft: getNumLeft,

	getNumCorrect: getNumCorrect,

	getNumProblems: getNumProblems,

	//Returns info to set up the StarProgress view and StarInfoModal
	//Only necessary for results where showStarProgress is true
	getStarProgressInfo: function(result) {
		const starCutoffs = getManager(result).getAvailableStarCutoffs(result);
		const starMessages = {};
		for (let i = 0; i < starCutoffs.length; i++) {
			starMessages[i + 1] =
				"Get " + _.nth(starCutoffs, i * -1 - 1) + " correct.";
		}

		return {
			styleType: result.styleType,
			filledAmount: getManager(result).getNumCorrect(result),
			totalAmount: getManager(result).getNumProblems(result),
			starsAvailable: result.starsAvailable,
			allCutoffs: result.starCutoffs,
			starCutoffs: starCutoffs,
			keepStarsReached: false,
			modalInfo: {
				showLimitedStarsMessage: result.starsAvailable !== 3,
				starMessages,
				resultType: result.resultType,
			},
		};
	},

	// Modifies the progress to save to the backend. Takes (result, defaultProgress)
	// where the second argument is what is saved by default.
	computeBackendProgress: (result, defaultProgress) => defaultProgress,

	getResultProgressXP,

	// Modifies the trial to save to the backend. Takes (result, problem, backendTrial)
	// where the third argument is what is saved by default.
	computeBackendTrial: (result, problem, backendTrial) => backendTrial,

	// Returns summary message to show on class report pages.
	// This takes in a backend result - problem is at result.progress.problems
	getSummaryForReport: backendResult => {
		const problems = _.get(backendResult, "progress.problems", []);
		const totalProblems = problems.length;
		const numCorrect = _.filter(problems, p => p.outcome === "correct");
		return `${numCorrect} / ${totalProblems} problems answered correctly.`;
	},

	//
	// Methods whose calling code should assume that when null is returned, a
	// default behavior should be used instead.
	//

	// Returns the set progress view to use and any data to pass to it.
	getSetProgressInfo: () => null,
};

//
// Result Manager implementation (base class, derived classes, etc.)
//

const specificManagerData = {
	//Main block types
	standard: require("./results/standard"),
	standardSequential: require("./results/standardSequential"),
	test: require("./results/test"),
	challenge: require("./results/challenge"),
	review: require("./results/review"),
	trophy: require("./results/trophy"),
	lab: require("./results/lab"),
	bookTest: require("./results/bookTest"),
	//Shared puzzle type
	puzzle: require("./results/puzzle"),
	//Special engines
	arithmeticDrill: require("./results/arithmeticDrill"),
	bubbleGame: require("./results/bubbleGame"),
	fillDrill: require("./results/fillDrill"),
	flashcardDrill: require("./results/flashcardDrill"),
	hiddenMessage: require("./results/hiddenMessage"),
	multiplicationChart: require("./results/multiplicationChart"),
	ropeClimbDrill: require("./results/ropeClimbDrill"), // Note: this is similar to arithmeticDrill
	snowballGame: require("./results/snowballGame"),
};

// Cache of managers constructed from data above.
const specificManagerInstances = {};

const BaseManager = _.extend({}, defaultSettings, defaultMethods);

const createManager = function(data) {
	const manager = Object.create(BaseManager);
	const dataSettings = _.pick(
		data.settings || {},
		Object.keys(defaultSettings)
	);
	const dataMethods = _.pick(data, Object.keys(defaultMethods));
	_.extend(manager, dataSettings, dataMethods);
	return manager;
};

const getManagerFromType = function(resultType) {
	if (!specificManagerData[resultType]) {
		throw new Error(
			"Result Manager of type " + resultType + " cannot be found."
		);
	}
	if (!specificManagerInstances[resultType]) {
		specificManagerInstances[resultType] = createManager(
			specificManagerData[resultType]
		);
	}
	return specificManagerInstances[resultType];
};

const getManager = function(result) {
	return getManagerFromType(
		result && result.resultType ? result.resultType : "standard"
	);
};

// Public interface.

const ResultManager = getManager;

ResultManager.forType = getManagerFromType;

// Allow a shorthand: ResultManager.method(result, ...) instead of
// ResultManager(result).method(result, ...)
Object.keys(defaultMethods).forEach(name => {
	ResultManager[name] = function(result, ...args) {
		return ResultManager(result)[name](result, ...args);
	};
});

module.exports = ResultManager;
